import { defineComponent as _defineComponent } from 'vue'
import { ref, onMounted, watch, computed } from "vue";
  import router from "@/router";
  import store from "@/store";
  import {
    listingstatuses,
    StatusListingFiltersLabels,
    StatusListingFiltersValues,
  } from "@/constants/statusFilters";
  import plural from "@/features/plural";
  import Modal from "@/components/ui/Modal.vue";
  import DropDown from "@/components/ui/dropdown/DropDown.vue";
  import InputSearch from "@/components/ui/input/InputSearch.vue";
  import { ListingStatus } from "@/enums/ListingStatus";
  import { getListingStatusClass } from "@/helpers/status";
  import { debounce } from "@/helpers/debounce";
  import { useToast } from "vue-toastification";
  import getUserRole from "@/helpers/getUserRole";
  import ConfigureAgentIdModal from "@/components/ui/modals/ConfigureAgentIdModal.vue";
  import ListingItem from "@/types/ListingItemInterface";
  import getUserData from "@/helpers/listing/getUserData";

  
export default /*@__PURE__*/_defineComponent({
  __name: 'ListingsView',
  setup(__props, { expose: __expose }) {
  __expose();

  const loading = ref(false);
  const isOpenedConfigureModal = ref(false);
  const totalListings = ref([]);
  const listingList = ref<any>([]);
  const activeFilter = ref(listingstatuses[0]);
  const toast = useToast();
  const filters = listingstatuses;
  const userId = computed(() => store.state.user.data._id);
  const estateId = computed(() => store.state.user.selectedEstateId);
  const userDetailsId = computed(() =>
    typeof store.state.user.data.detailsId === "string"
      ? store.state.user.data.detailsId
      : store.state.user.data.detailsId._id
  );
  const { isLandownerRole } = getUserRole();
  const selectedEstate = computed(() => store.state.user.selectedEstateId);
  const agentId = getUserData().agentId;
  let canCreateListing = Boolean(agentId);

  const saveAgentId = async (agentId: Event) => {
    try {
      const res = await store.dispatch("estate/updateAgentId", {
        agentId,
        id: selectedEstate.value,
      });

      if (res.status === 200) {
        await store.dispatch("user/refreshAgentId", agentId);
        toast.success("AgentID updated succesfully");
        canCreateListing = true;
        isOpenedConfigureModal.value = false;
      }
    } catch (err) {
      toast.error("Something went wrong, try later");
      isOpenedConfigureModal.value = false;
    }
  };

  onMounted(() => {
    getListings().then(() => {
      totalListings.value = listingList.value;
    });
  });

  const openCreateListing = () => {
    if (!canCreateListing) {
      return toast.error("First configure the Agent ID");
    }

    return router.push("/listing/create");
  };

  const openConfigureModal = () => {
    isOpenedConfigureModal.value = true;
  };

  const onCloseConfigureAgentModal = () => {
    isOpenedConfigureModal.value = false;
  };

  const actionText = (status: ListingStatus): string | void => {
    const isDraft = status === ListingStatus.DRAFT;

    if (isDraft) {
      return "Publish";
    }

    if (status === ListingStatus.SOLD) {
      return;
    }

    return status !== ListingStatus.DEACTIVATED ? "Deactivate" : "Publish";
  };

  const editListing = (id: string) => router.push(`/listing/edit/${id}`);

  const handleAction = async (status: ListingStatus, id: string) => {
    if (status === ListingStatus.DRAFT) {
      return editListing(id);
    }

    if (status !== ListingStatus.DEACTIVATED) {
      return desactiveListing(id);
    }

    return activeListing(id);
  };

  const getListings = async () => {
    const status =
      activeFilter.value.value !== StatusListingFiltersValues.ANY
        ? activeFilter.value.value
        : "";

    try {
      loading.value = true;
      const res = await store.dispatch("listing/getListings", {
        status,
        estateId: estateId.value,
      });
      const listings = res.data.map((item: ListingItem) => {
        return {
          id: item._id,
          salesPersonid: item.salesPersonListingId,
          status: item.status,
          listingSite: item.listingSite || "N/A",
          title: `${item.address.street} ${item.address.streetNumber}, ${
            item.address.suburb ? `${item.address.suburb}, ` : ""
          }${item.address.state}`,
        };
      });

      if (isLandownerRole) {
        listingList.value = listings;
        return;
      }

      listingList.value = listings.filter(
        (item: ListingItem) => item.salesPersonid === userId.value
      );
    } catch (error) {
      toast.error("An error occurred while getting the listings");
    } finally {
      loading.value = false;
    }
  };

  const selectFilter = (filter: {
    value: StatusListingFiltersValues;
    label: StatusListingFiltersLabels;
  }) => {
    activeFilter.value = filter;
    getListings();
  };

  const activeListing = async (id: string) => {
    if (!agentId) {
      return toast.error("You need to set up your REA AgentId");
    }

    try {
      await store.dispatch("listing/activateListing", {
        id,
        agentId,
      });
      toast.success("Listing Published");
      return getListings();
    } catch (error) {
      return toast.error("Error on publish listing");
    }
  };

  const desactiveListing = async (id: string) => {
    const data = {
      id,
      agentId: agentId || "",
    };

    if (!data.agentId) {
      return toast.error("You need to set up your REA AgentId");
    }

    try {
      await store.dispatch("listing/desactivateListing", {
        id,
        agentId,
      });
      toast.success("Listing Deactivated");
      return getListings();
    } catch (error) {
      return toast.error("Error on Deactivate listing");
    }
  };

  const titles = [
    {
      text: "Title",
    },
    {
      text: "Site",
    },
    {
      text: "Status",
    },
  ];

const __returned__ = { loading, isOpenedConfigureModal, totalListings, listingList, activeFilter, toast, filters, userId, estateId, userDetailsId, isLandownerRole, selectedEstate, agentId, get canCreateListing() { return canCreateListing }, set canCreateListing(v) { canCreateListing = v }, saveAgentId, openCreateListing, openConfigureModal, onCloseConfigureAgentModal, actionText, editListing, handleAction, getListings, selectFilter, activeListing, desactiveListing, titles, ref, onMounted, watch, computed, get router() { return router }, get store() { return store }, get listingstatuses() { return listingstatuses }, get StatusListingFiltersLabels() { return StatusListingFiltersLabels }, get StatusListingFiltersValues() { return StatusListingFiltersValues }, get plural() { return plural }, Modal, DropDown, InputSearch, get ListingStatus() { return ListingStatus }, get getListingStatusClass() { return getListingStatusClass }, get debounce() { return debounce }, get useToast() { return useToast }, get getUserRole() { return getUserRole }, ConfigureAgentIdModal, get ListingItem() { return ListingItem }, get getUserData() { return getUserData } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})